import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LuPlus, LuBell } from "react-icons/lu";
import { FiSearch } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import NotificationTable from "./NotificationTable";
import NotificationView from "./NotificationView";
import ReactModal from "../ReactModal";

import { useGetNotificationsQuery } from "../../store/services/notificationServices";
import { notificationViewActions } from "../../store/services/notificationViewSlice";
import { hasMinimumRole, ROLES } from "../../utils/roleUtils";

const PAGE_LIMIT = 10;

export default function NotificationsMain() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");

  const { data, isLoading, isFetching } = useGetNotificationsQuery(params);

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    searchParams.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (searchParams.has("page")) setCurrentPage(parseInt(searchParams.get("page")));
    if (searchParams.has("q")) setSearchTextInput(searchParams.get("q"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredNotifications(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const filtered = Object.entries(params || {})
      .filter(([_, v]) => v !== null && v !== undefined && v !== "")
      .reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {});
    setSearchParams(filtered, { replace: true });
  }, [params, setSearchParams]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (notification) => {
    dispatch(notificationViewActions.openModal(notification));
  };

  const hasFilters = !!params.q;

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredNotifications.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      <ReactModal />
      <NotificationView />

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuBell size={18} />, label: "Njoftimet" }}
          title="Menaxho njoftimet"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} njoftime në sistem`
              : "Lista e njoftimeve të dërguara"
          }
          actions={
            hasMinimumRole(ROLES.OFFICIAL_1) && (
              <button
                type="button"
                onClick={() => navigate("/notifications/add")}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
              >
                <LuPlus size={18} />
                Dërgo njoftim të ri
              </button>
            )
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas titullit ose tipit..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuBell}
            title={hasFilters ? "Asnjë njoftim nuk u gjet" : "Ende asnjë njoftim"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Njoftimet e dërguara do të shfaqen këtu."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <NotificationTable
            notifications={filteredNotifications}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
}
